import { Injectable } from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormGroup,
  ValidatorFn
} from '@angular/forms';
import { LocalizationModel, ObjectUtils, ToFormGroup } from '@shared/core';
import {
  FormFieldType,
  IFormGroupConfig,
  IFormGroupFieldConfig,
} from '../models';
import { FormValidators } from '../validators';

@Injectable({
  providedIn: 'root',
})
export class FormBuilderService {
  private isPathValueMode = false;

  build(
    config: IFormGroupConfig,
    values?: NonNullable<unknown>,
    validator?: ValidatorFn[]
  ) {
    const group = new UntypedFormGroup({}, validator);
    this.isPathValueMode = values != null;
    this.addFormControl(group, config?.fields ?? {}, values);
    return group;
  }

  private addFormControl(
    form: FormGroup,
    fields: IFormGroupFieldConfig | IFormGroupConfig,
    values?: NonNullable<unknown>
  ): void {
    if (fields == null) {
      return;
    }
    Object.entries(fields).forEach(([fieldName, fieldConfig]) => {
      let formControl;
      const formValue = this.getFieldValue(fieldName, fieldConfig, values);

      if (fieldConfig.type === FormFieldType.GROUP) {
        if (fieldConfig.forWrapping) {
          this.addFormControl(form, fieldConfig.fields ?? {}, values);
        } else {
          formControl = new FormGroup({}, fieldConfig.validators);
          this.addFormControl(formControl, fieldConfig.fields ?? {}, formValue);
        }
      } else if (fieldConfig.type === FormFieldType.CHECKBOX) {
        const validators = fieldConfig.required
          ? [FormValidators.requiredCheckbox]
          : [];
        if (fieldConfig.validators) {
          validators.push(...fieldConfig.validators);
        }
        formControl = new FormControl<unknown>(formValue ?? false, validators);
        if (fieldConfig.disabled) {
          formControl.disable();
        }
      } else {
        const validators = fieldConfig.required
          ? fieldConfig.translatable
            ? [FormValidators.translatableFieldRequiredValidator as ValidatorFn]
            : [FormValidators.required]
          : [];
        if (fieldConfig.validators) {
          validators.push(...fieldConfig.validators);
        }
        formControl = fieldConfig.translatable
          ? new FormGroup<ToFormGroup<LocalizationModel>>(
              {
                kz: new FormControl(formValue && formValue['kz']),
                ru: new FormControl(formValue && formValue['ru']),
              },
              validators
            )
          : new FormControl<unknown>(formValue, validators);

        if (fieldConfig.disabled) {
          formControl.disable();
        }
      }

      if (formControl) {
        form.addControl(fieldName, formControl);
      }
    });
  }

  private getFieldValue(
    fieldName: string,
    fieldConfig: IFormGroupFieldConfig | IFormGroupConfig,
    values: any
  ): any {
    if (this.isPathValueMode) {
      return values != null ? values[fieldName] : undefined;
    } else {
      return ObjectUtils.getPropertyValue(fieldConfig, 'value') ?? undefined;
    }
  }

  getFieldConfigByName(
    fieldName: string,
    fields: IFormGroupConfig
  ): any | undefined {
    if (fields) {
      for (const [name, config] of Object.entries(fields)) {
        if (name === fieldName) {
          return config;
        }

        if (typeof config === 'object' && config) {
          const foundField = this.getFieldConfigByName(fieldName, config);
          if (foundField) {
            return foundField;
          }
        }
      }
    }

    return undefined;
  }
}
